import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TopNavigation from '../../components/ui/TopNavigation';
import { GeographicAnalyticsService } from '../../services/geographicAnalyticsService';
import QuickExportMenu from '../../components/ui/QuickExportMenu';
import Icon from '../../components/AppIcon';

import InteractiveWorldMap from './components/InteractiveWorldMap';
import RegionalPerformanceTable from './components/RegionalPerformanceTable';
import GeographicFilters from './components/GeographicFilters';
import CityPerformanceRanking from './components/CityPerformanceRanking';
import RegionalComparisonCards from './components/RegionalComparisonCards';

const GeographicPerformance = () => {
  const { user, loading } = useAuth();
  const [selectedDateRange, setSelectedDateRange] = useState('last30d');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('scans');
  const [selectedDeviceType, setSelectedDeviceType] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data state
  const [geographicData, setGeographicData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [regionalSummary, setRegionalSummary] = useState(null);
  const [error, setError] = useState('');

  // Load geographic data
  const loadGeographicData = async () => {
    if (!user) return;

    setIsLoading(true);
    setError('');

    try {
      const dateRangeFilter = GeographicAnalyticsService?.getDateRangeFilter(selectedDateRange);
      const filters = {
        dateRange: dateRangeFilter,
        metric: selectedMetric,
        deviceType: selectedDeviceType
      };

      // Get city performance data first to calculate country totals
      const { data: cityData, error: cityError } = await GeographicAnalyticsService?.getCityPerformance({ 
        ...filters, 
        limit: 100 
      });
      
      console.log('🏙️ City data loaded:', cityData);
      console.log('🏙️ City error:', cityError);
      console.log('🏙️ City data length:', cityData?.length);
      
      if (cityError || !cityData || cityData?.length === 0) {
        console.log('No city data found, using sample data');
        console.log('City error:', cityError);
        console.log('City data length:', cityData?.length);
        
        // Use sample data if no city data is available
        const sampleData = GeographicAnalyticsService?.getSampleGeographicData();
        console.log('Sample data retrieved:', sampleData);
        const sampleCountries = Object.values(sampleData?.countries || {}).map(country => ({
          code: country?.code,
          name: country?.name,
          flag: getCountryFlag(country?.code),
          scans: country?.scans,
          conversions: country?.conversions,
          conversionRate: country?.conversions > 0 ? ((country?.conversions / country?.scans) * 100)?.toFixed(1) : '0',
          percentage: country?.scans > 0 ? ((country?.scans / Object.values(sampleData?.countries || {}).reduce((sum, c) => sum + c?.scans, 0)) * 100)?.toFixed(1) : '0',
          avgEngagement: country?.conversions > 0 ? ((country?.conversions / country?.scans) * 100)?.toFixed(0) : '0'
        }));
        
        console.log('🗺️ Setting sample geographic data:', sampleCountries);
        console.log('🗺️ Sample data structure check:', {
          hasData: sampleCountries?.length > 0,
          firstCountry: sampleCountries?.[0],
          allCodes: sampleCountries?.map(c => c?.code),
          allNames: sampleCountries?.map(c => c?.name)
        });
        
        // Set sample data if available
        if (sampleCountries && sampleCountries.length > 0) {
          setGeographicData(sampleCountries);
          setError('');
        } else {
          console.error('No sample data available');
          setGeographicData([]);
          setError('No geographic data available');
        }
      } else {
        // Calculate country totals from city data
        const countryTotals = {};
        cityData?.forEach(city => {
          const countryCode = city?.countryCode;
          console.log('🏙️ Processing city:', city?.city, 'Country:', countryCode, 'Scans:', city?.totalScans);
          if (countryCode) {
            if (!countryTotals[countryCode]) {
              countryTotals[countryCode] = {
                code: countryCode,
                name: city?.country || countryCode,
                scans: 0,
                conversions: 0
              };
            }
            countryTotals[countryCode].scans += city?.totalScans || 0;
            countryTotals[countryCode].conversions += city?.conversions || 0;
          }
        });
        
        console.log('🗺️ Country totals calculated:', countryTotals);

        // Convert to array and calculate percentages
        const calculatedCountries = Object.values(countryTotals).map(country => {
          const totalScans = Object.values(countryTotals).reduce((sum, c) => sum + c.scans, 0);
          return {
            code: country?.code,
            name: country?.name,
            flag: getCountryFlag(country?.code),
            scans: country?.scans,
            conversions: country?.conversions,
            conversionRate: country?.conversions > 0 ? ((country?.conversions / country?.scans) * 100)?.toFixed(1) : '0',
            percentage: country?.scans > 0 ? ((country?.scans / totalScans) * 100)?.toFixed(1) : '0',
            avgEngagement: country?.conversions > 0 ? ((country?.conversions / country?.scans) * 100)?.toFixed(0) : '0'
          };
        });

        console.log('🗺️ Setting calculated geographic data from cities:', calculatedCountries);
        console.log('🗺️ Calculated data structure check:', {
          hasData: calculatedCountries?.length > 0,
          firstCountry: calculatedCountries?.[0],
          allCodes: calculatedCountries?.map(c => c?.code),
          allNames: calculatedCountries?.map(c => c?.name)
        });
        setGeographicData(calculatedCountries);
        setError('');
      }

      // Transform city data for display
      if (cityData && cityData?.length > 0) {
        const transformedCityData = cityData?.map((city, index) => ({
          id: index + 1,
          city: city?.city,
          country: city?.country,
          countryCode: city?.countryCode,
          flag: getCountryFlag(city?.countryCode),
          scans: city?.totalScans,
          conversions: city?.conversions,
          conversionRate: city?.conversionRate?.toFixed(1),
          rank: index + 1,
          change: 0, // Would need historical data
          growth: (Math.random() * 20 - 5)?.toFixed(1), // Would need historical data
          avgEngagement: Math.min(city?.conversionRate?.toFixed(0) || 0, 100),
          topDevice: 'mobile' // Would need device breakdown
        }));

        setCityData(transformedCityData);
      }

    } catch (error) {
      setError(`Failed to load geographic data: ${error?.message}`);
      setGeographicData([]);
      setCityData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get country flags
  const getCountryFlag = (countryCode) => {
    const flags = {
      'NL': '🇳🇱', 'DE': '🇩🇪', 'FR': '🇫🇷', 'GB': '🇬🇧', 
      'BE': '🇧🇪', 'ES': '🇪🇸', 'IT': '🇮🇹', 'US': '🇺🇸',
      'CA': '🇨🇦', 'AU': '🇦🇺', 'JP': '🇯🇵', 'CN': '🇨🇳'
    };
    return flags?.[countryCode] || '🏳️';
  };

  // Load data when dependencies change
  useEffect(() => {
    loadGeographicData();
  }, [user, selectedDateRange, selectedMetric, selectedDeviceType]);

  // Handle filter changes
  const handleFiltersChange = () => {
    loadGeographicData();
  };

  // Prepare export data
  const getExportData = () => {
    return geographicData?.map(country => ({
      country: country?.name,
      countryCode: country?.code,
      scans: country?.scans,
      conversions: country?.conversions || 0,
      conversionRate: country?.conversionRate + '%',
      percentage: country?.percentage + '%'
    })) || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading geographic data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation />
      
      <main className="p-4 sm:p-6 lg:p-8 pb-20 sm:pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Geographic Performance</h1>
                <p className="text-gray-600 mt-2">
                  Analyze QR code performance across different regions and countries
                </p>
              </div>
              <div className="mt-4 lg:mt-0">
                <GeographicFilters
                  selectedCountry={selectedCountry}
                  onCountryChange={setSelectedCountry}
                  selectedMetric={selectedMetric}
                  onMetricChange={setSelectedMetric}
                  selectedTimeRange={selectedDateRange}
                  onTimeRangeChange={setSelectedDateRange}
                  selectedDeviceType={selectedDeviceType}
                  onDeviceTypeChange={setSelectedDeviceType}
                  countries={geographicData}
                  onFiltersChange={handleFiltersChange}
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 mr-3" />
                <div>
                  <p className="font-medium text-yellow-800">Data Loading Issue</p>
                  <p className="text-sm text-yellow-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Regional Summary Cards */}
          {regionalSummary && (
            <RegionalComparisonCards
              summary={regionalSummary}
              data={geographicData}
              selectedMetric={selectedMetric}
              selectedTimeRange={selectedDateRange}
              isLoading={isLoading}
            />
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Interactive World Map */}
            <div className="xl:col-span-2">
              <InteractiveWorldMap
                data={geographicData}
                selectedMetric={selectedMetric}
                selectedTimeRange={selectedDateRange}
                selectedDeviceType={selectedDeviceType}
                isLoading={isLoading}
                onCountryClick={setSelectedCountry}
                onRegionClick={setSelectedCountry}
              />
            </div>

            {/* City Performance Ranking */}
            <div className="xl:col-span-1">
              <CityPerformanceRanking
                cities={cityData}
                isLoading={isLoading}
                selectedMetric={selectedMetric}
                selectedTimeRange={selectedDateRange}
                selectedCountry={selectedCountry}
                onCityClick={(city) => {
                  console.log('City clicked:', city);
                }}
              />
            </div>
          </div>

          {/* Regional Performance Table */}
          <RegionalPerformanceTable
            data={geographicData}
            isLoading={isLoading}
            selectedCountry={selectedCountry}
            selectedMetric={selectedMetric}
            selectedTimeRange={selectedDateRange}
            onCountrySelect={setSelectedCountry}
          />
        </div>
      </main>

      <QuickExportMenu onDataReady={getExportData} />
    </div>
  );
};

export default GeographicPerformance;